import React, { memo } from 'react';
import { Chip as MuiChip } from '@mui/material';

import { useStyles } from './styles';
import ControlledError from '../ControlledError';
import { ReactComponent as CrossDeleteIcon } from 'assets/icons/CrossDeleteIcon.svg';

export interface IChipProps {
  chip: { value: string; label: string; chipProps?: any };
  variant?: 'filled' | 'outlined';
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  hasRemove?: boolean;
  disableRipple?: boolean;
  styleClass?: string;
  disabled?: boolean;
  testId?: string;
  onClick?: () => void;
  onRemove?: () => void;
}
export interface IChipsProps {
  chips: { value: string; label: string; chipProps?: any }[];
  selectedChips: { value: string; label: string }[];
  extraChips?: { value: string; label: string; chipProps?: any }[];
  variant?: 'filled' | 'outlined';
  error?: string;
  styleClass?: string;
  disabled?: boolean;
  /** Base id for automation. Selectable chips get `${testId}-chip-{value}`, extra chips
   * `${testId}-extra-chip-{value}`. */
  testId?: string;
  onChange: (selectedChips: { value: string; label: string }[]) => void;
  onExtraChipRemove?: (chip: { value: string; label: string }) => void;
}

export const Chip = ({
  chip,
  variant,
  color,
  hasRemove,
  disableRipple,
  styleClass,
  disabled,
  testId,
  onClick,
  onRemove,
}: IChipProps) => {
  const classes = useStyles();
  return (
    <MuiChip
      label={chip.label}
      data-testid={testId}
      variant={variant || 'outlined'}
      color={color || 'primary'}
      disableRipple={disableRipple}
      className={`${classes.chip} ${hasRemove ? classes.selectedChip : ''} ${styleClass || ''}`}
      deleteIcon={hasRemove && !disabled && <CrossDeleteIcon className={classes.deleteIcon} />}
      onClick={onClick}
      onDelete={hasRemove && !disabled && onRemove}
      disabled={disabled}
      {...(chip.chipProps || {})}
    />
  );
};

const Chips = ({
  chips,
  selectedChips,
  extraChips = [],
  variant,
  styleClass,
  onChange,
  onExtraChipRemove,
  error,
  disabled,
  testId,
}: IChipsProps) => {
  const classes = useStyles();
  return (
    <ControlledError show={!!error} error={error}>
      <div className={`${classes.chipsContainer} ${styleClass || ''}`} data-testid={testId}>
        {/* Regular selectable chips */}
        {chips.map(chip => (
          <Chip
            key={chip.value || chip.label}
            chip={chip}
            variant={variant || 'outlined'}
            color={'default'}
            styleClass={selectedChips.find(c => c.value === chip.value) ? classes.selectedChip : ''}
            disableRipple
            disabled={disabled}
            testId={testId ? `${testId}-chip-${chip.value || chip.label}` : undefined}
            onClick={() => {
              const newSelectedChips = selectedChips.find(c => c.value === chip.value)
                ? selectedChips.filter(c => c.value !== chip.value)
                : [...selectedChips, { value: chip.value, label: chip.label }];
              onChange(newSelectedChips);
            }}
            {...chip.chipProps}
          />
        ))}

        {/* Extra chips with delete functionality */}
        {extraChips.map(chip => (
          <Chip
            key={`extra-${chip.value || chip.label}`}
            chip={chip}
            variant={variant || 'outlined'}
            color={'primary'}
            hasRemove={true}
            disabled={disabled}
            testId={testId ? `${testId}-extra-chip-${chip.value || chip.label}` : undefined}
            onRemove={onExtraChipRemove ? () => onExtraChipRemove(chip) : undefined}
            {...chip.chipProps}
          />
        ))}
      </div>
    </ControlledError>
  );
};

export default memo(Chips);
